import { useMemo } from 'react';
import { FERTILITY_DEMO_STATUS_PILL_TEXT } from '../data/pageHeaderConfig';
import { formatIvfTimelineCallout, ivfTimelineFromCycle } from '../utils/ivfCycleTimeline';

/** Chapter-specific stage display for non-IVF. */
const CHAPTER_STAGE_LABELS = {
  ttc: { icon: '💜', label: 'Cycle day 14 · Fertile window' },
  fertility: { icon: '🌸', label: FERTILITY_DEMO_STATUS_PILL_TEXT },
  ivf: { icon: '🌸', label: FERTILITY_DEMO_STATUS_PILL_TEXT },
  pregnancy: { icon: '🤰', label: 'Week 20 · Second trimester' },
  postpartum: { icon: '🤱', label: '6 weeks postpartum' },
  loss: { icon: '🌸', label: 'Fertility' },
};

/**
 * Derives a human-readable stage label from journey/cycle data.
 * @param {{ user, journey, activeCycle }} from useApp
 * @param {{ chapter, demoLabel, demoIcon }} options - chapter for non-IVF, demo overrides when signed out
 * @returns {{ label: string, icon: string }}
 */
export function useStageLabel(appData, options = {}) {
  const { user, journey, activeCycle } = appData || {};
  const { chapter = 'fertility', demoLabel, demoIcon } = options;

  return useMemo(() => {
    // Explicit demo override (signed-out / custom pill text)
    if (demoLabel != null && demoLabel !== '') {
      return {
        icon: demoIcon ?? CHAPTER_STAGE_LABELS.fertility?.icon ?? '🌸',
        label: demoLabel,
      };
    }

    const ch = String(chapter || 'fertility').toLowerCase();
    const isIVF = ['ivf', 'fertility'].includes(ch);

    // Signed-in + real journey data
    if (user && journey) {
      const cycles = journey?.cycles || [];
      const current = activeCycle || (journey?.activeCycleId && cycles.find(c => c.id === journey.activeCycleId)) || cycles[0];
      const cycleNum = cycles.length;

      if (current && isIVF) {
        const stimDay = current.stimDay ?? (current.startedAt
          ? Math.max(0, Math.floor((Date.now() - new Date(current.startedAt + 'T00:00:00').getTime()) / 86400000))
          : null);
        const hasStimDay = stimDay != null && stimDay > 0;
        const estimatedRetrieval = current.estimatedRetrieval || current.retrievalDate;
        const retrievalDate = estimatedRetrieval ? new Date(estimatedRetrieval + 'T00:00:00') : null;
        const transferDate = current.transferDate ? new Date(current.transferDate + 'T00:00:00') : null;
        const today = new Date();

        if (hasStimDay) {
          const timeline = ivfTimelineFromCycle(current, cycles);
          const line = formatIvfTimelineCallout(timeline);
          return { icon: '🌸', label: line || `Cycle ${cycleNum} · Stim day ${stimDay}` };
        }
        if (transferDate && today >= transferDate) {
          return { icon: '🌸', label: `Cycle ${cycleNum} · Two-week wait` };
        }
        if (retrievalDate && today >= retrievalDate) {
          return { icon: '🌸', label: `Cycle ${cycleNum} · Post-retrieval` };
        }
        if (current.stage && !['ivf', 'fertility'].includes(String(current.stage))) {
          return { icon: '🌸', label: `Cycle ${cycleNum} · ${current.stage}` };
        }
        return { icon: '🌸', label: `Cycle ${cycleNum} · In progress` };
      }
    }

    // Fallback: chapter-specific demo label
    const chapterInfo = CHAPTER_STAGE_LABELS[ch] || CHAPTER_STAGE_LABELS.fertility;
    return {
      icon: chapterInfo?.icon ?? '🌸',
      label: chapterInfo?.label ?? 'Fertility',
    };
  }, [user, journey, activeCycle, chapter, demoLabel, demoIcon]);
}
